/**
 * Phase F.1b-ii (sub-task under GEN-2998) — write-side comparator adapter.
 *
 * Symmetric counterpart to D.4's `comparatorReadAdapter`. D.4 covers the
 * **hydration** boundary: when the server returns a canonical payload
 * carrying `comparators[]`, the read adapter non-destructively projects
 * MOTHER / FATHER rows into the legacy `mother` / `father` slots so the
 * existing read pipeline keeps working unchanged.
 *
 * This file covers the **live-session write** boundary: when a Redux
 * reducer mutates a legacy `mother` / `father` slot, the
 * `applyLegacyParentSlotChangeToComparators` helper keeps any matching
 * `Comparator` in `comparators[]` in sync within the session, so other
 * consumers reading from the new shape never observe stale data.
 *
 * Field mapping (forward direction — slot → comparators[]):
 *
 *     id                  ↔ id
 *     demographicId       ↔ demographic.demographicId
 *     parentFirstName     ↔ demographic.firstName
 *     parentLastName      ↔ demographic.lastName
 *     parentDateOfBirth   ↔ demographic.dateOfBirth
 *     parentMRN           ↔ demographic.mrn
 *     parentEmail         ↔ demographic.email
 *     parentPhone         ↔ demographic.phone
 *     isPrimaryGuardian   ↔ isPrimaryGuardian
 *
 * The reverse direction (`applyComparatorUpsertToLegacyParentSlot`)
 * additionally projects `specimen`, `secondaryConsent`, and clinical
 * fields (`phenotypicTrait`, `clinicalNote`, `icd10Terms`) onto the
 * legacy slot — see that helper's docstring for the full per-field
 * contract. The forward direction stays demographic-only because the
 * read pipeline already has `enrichComparatorFromLegacySlot` lifting
 * those non-demographic fields from the slot to the comparator on
 * hydration.
 *
 * Semantics:
 *   - Slot becomes `null` → the matching MOTHER / FATHER comparator is
 *     **removed** from `comparators[]` (mirrors "no parent testing
 *     requested").
 *   - Slot becomes populated and a matching comparator exists → its
 *     identity fields are refreshed; **all other fields are preserved**
 *     (specimen, secondaryConsent, hpoTerms, etc.).
 *   - Slot becomes populated and no matching comparator exists → a new
 *     Comparator is appended with the canonical relationship literal.
 *   - When nothing needs to change, the helper returns the input array
 *     by reference so React / Redux selectors don't see a phantom diff.
 *
 * Both directions are exported. The forward direction (legacy slot
 * change → `comparators[]`) is wired into the canonical-order slice in
 * F.1b-ii. The reverse direction
 * (`applyComparatorUpsertToLegacyParentSlot`) is dormant infrastructure
 * for F.1b-iii (the `upsertComparator` reducer that will accompany the
 * `<ComparatorModal>` edit wire-up).
 */

import { genComparatorId } from '@/ordering/genComparatorId'
import type {
  CanonicalOrderPayload,
  CanonicalOrderPayloadInbound,
  CanonicalParentInfo,
  Comparator,
  ComparatorDemographic,
  ComparatorSecondaryConsent,
  ComparatorSpecimen,
  FamilyMemberRelationship,
} from './canonicalOrder'

/**
 * Internal slot-key shape (`'mother' | 'father'`) used by the hydration-boundary
 * normalizer (`normalizeInboundParentComparators`) and `parentSideForComparator`
 * to identify the 1-of-1 parent rows. It remains the role discriminator shared
 * with `comparatorReadAdapter` (`getParentComparator`, `orderTypeFromComparators`,
 * `parentComparatorCountForOrder`); full removal is gated on the Phase E
 * hydration rewrite that retires the legacy `mother` / `father` inbound slots.
 */
export type ParentSide = 'mother' | 'father'

const MOTHER_LITERALS: ReadonlySet<string> = new Set(['MOTHER', 'bio_mother'])
const FATHER_LITERALS: ReadonlySet<string> = new Set(['FATHER', 'bio_father'])

function canonicalLiteralForSide(side: ParentSide): 'MOTHER' | 'FATHER' {
  return side === 'mother' ? 'MOTHER' : 'FATHER'
}

function relationshipMatches(
  value: FamilyMemberRelationship | string | null | undefined,
  set: ReadonlySet<string>
): boolean {
  if (value == null) return false
  return set.has(String(value).trim())
}

function strOrUndefined(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') return undefined
  const t = value.trim()
  return t === '' ? undefined : t
}

/**
 * True when the slot is populated enough to project (has at least one
 * identity field). Mirrors `needsProjection` in `comparatorReadAdapter`
 * so the round-trip is stable: a slot with only `collectionType: BLOOD`
 * (autosave artifact) is treated as empty here.
 */
function slotHasMeaningfulIdentity(slot: CanonicalParentInfo | null | undefined): boolean {
  if (slot == null) return false
  const id = strOrUndefined(slot.id)
  const demoId = strOrUndefined(slot.demographicId)
  const first = strOrUndefined(slot.parentFirstName)
  const last = strOrUndefined(slot.parentLastName)
  const dob = strOrUndefined(slot.parentDateOfBirth)
  const mrn = strOrUndefined(slot.parentMRN)
  const email = strOrUndefined(slot.parentEmail)
  const phone = strOrUndefined(slot.parentPhone)
  return Boolean(id || demoId || first || last || dob || mrn || email || phone)
}

function buildComparatorFromSlot(slot: CanonicalParentInfo, relationship: 'MOTHER' | 'FATHER'): Comparator {
  const demo: ComparatorDemographic = {}
  const id = strOrUndefined(slot.id)
  const demoId = strOrUndefined(slot.demographicId)
  const first = strOrUndefined(slot.parentFirstName)
  const last = strOrUndefined(slot.parentLastName)
  const dob = strOrUndefined(slot.parentDateOfBirth)
  const sex = strOrUndefined(slot.parentSex)
  const mrn = strOrUndefined(slot.parentMRN)
  const email = strOrUndefined(slot.parentEmail)
  const phone = strOrUndefined(slot.parentPhone)
  if (first) demo.firstName = first
  if (last) demo.lastName = last
  if (dob) demo.dateOfBirth = dob
  if (sex) demo.sex = sex
  if (mrn) demo.mrn = mrn
  if (email) demo.email = email
  if (phone) demo.phone = phone
  if (demoId) demo.demographicId = demoId
  const out: Comparator = { relationship, demographic: demo }
  if (id) out.id = id
  if (typeof slot.isPrimaryGuardian === 'boolean') {
    out.isPrimaryGuardian = slot.isPrimaryGuardian
  }
  // Plan A.2: reverse-bridge symmetry. Project consent / specimen / clinical
  // fields from the slot so a populated parent slot that carries them lands
  // them on the synthesized comparator too. Mirrors the forward-direction
  // projection in `applyComparatorUpsertToLegacyParentSlot`. Without this,
  // C1.1+C1.2 read surfaces (which prefer the comparator) would observe
  // stale consent/specimen/clinical state after hydrating from such slots.
  const sc = buildSecondaryConsentFromSlot(slot)
  if (sc) out.secondaryConsent = sc
  const sp = buildSpecimenFromSlot(slot)
  if (sp) out.specimen = sp
  if (slot.phenotypicTrait !== undefined) out.phenotypicTrait = slot.phenotypicTrait ?? null
  if (slot.clinicalNote !== undefined) out.clinicalNote = slot.clinicalNote ?? null
  if (slot.icd10Terms !== undefined) out.icd10Terms = slot.icd10Terms ?? null
  if (slot.hpoTerms !== undefined) out.hpoTerms = slot.hpoTerms ?? []
  return out
}

/**
 * Plan A.2 helper: build a `ComparatorSecondaryConsent` block from the
 * three flat consent flags on a legacy slot, returning `undefined` when
 * none of them are defined (so we don't add an empty block to the
 * comparator unnecessarily).
 */
function buildSecondaryConsentFromSlot(slot: CanonicalParentInfo): ComparatorSecondaryConsent | undefined {
  const incDef = slot.includeIncidentalFindings !== undefined
  const nyDef = slot.newYorkSampleRetentionOptIn !== undefined
  const rcDef = slot.researchContactConsent !== undefined
  if (!incDef && !nyDef && !rcDef) return undefined
  const out: ComparatorSecondaryConsent = {}
  if (incDef) out.includeIncidentalFindings = slot.includeIncidentalFindings ?? null
  if (nyDef) out.newYorkSampleRetentionOptIn = slot.newYorkSampleRetentionOptIn ?? null
  if (rcDef) out.researchContactConsent = slot.researchContactConsent ?? null
  return out
}

/**
 * Plan A.2 helper: build a `ComparatorSpecimen` block from the flat
 * `specimenId` / `collectionType` slot fields. Returns `undefined` when
 * neither is defined.
 */
function buildSpecimenFromSlot(slot: CanonicalParentInfo): ComparatorSpecimen | undefined {
  const idDef = slot.specimenId !== undefined
  const ctDef = slot.collectionType !== undefined
  if (!idDef && !ctDef) return undefined
  const out: ComparatorSpecimen = {}
  if (idDef) out.specimenId = slot.specimenId ?? null
  if (ctDef) out.collectionType = slot.collectionType ?? null
  return out
}

/**
 * Hydration-boundary normalizer (GEN-3393).
 *
 * `CanonicalOrderPayload` no longer carries `mother` / `father` slots, but
 * payloads persisted before the migration (and any in-flight ordering session
 * JSON) may still arrive with them. This fold:
 *   1. synthesizes a `MOTHER` / `FATHER` `Comparator` for every populated
 *      legacy slot that lacks a matching `comparators[]` row, then
 *   2. strips the legacy `mother` / `father` keys,
 * so the rest of the app only ever sees the comparators-only shape.
 *
 * Additive for comparators — never removes or mutates an existing comparator
 * row (the comparator side wins when both shapes are present). Returns a clean
 * `CanonicalOrderPayload` with no `mother` / `father` keys.
 */
/**
 * True when an inbound parent slot represents the **primary guardian only**
 * (a consenter/guardian who is not a sequenced comparator), not a tested
 * parent. Such a person is surfaced solely via the Guardian Contact card, so
 * folding them into `comparators[]` would spuriously render a Mother/Father
 * comparator group in the R5 side-nav on reload (the order is PROBAND-only).
 *
 * A parent is "guardian-only" when BOTH hold:
 *   1. their side matches the guardian relationship — `guardianContact`
 *      `relationshipToPatient` (MOTHER/FATHER) or the primary-consent family
 *      member relationship (`bio_mother`/`bio_father`); AND
 *   2. the slot carries no specimen (no `collectionType` / `specimenId`), i.e.
 *      no sample was collected for sequencing.
 *
 * A guardian who also provides a sample (has specimen/collectionType) is a
 * real comparator and still folds in. This mirrors the read-side intent that
 * "a primary guardian need not be a comparator".
 */
function slotIsGuardianOnlyParent(
  co: CanonicalOrderPayloadInbound,
  side: ParentSide,
  slot: CanonicalParentInfo
): boolean {
  const guardianRel = (co.guardianContact?.relationshipToPatient ?? '').trim().toUpperCase()
  const pcmRel = (co.primaryConsent?.familyMember?.relationship ?? '').trim().toLowerCase()
  const matchesGuardian =
    guardianRel === canonicalLiteralForSide(side) || pcmRel === (side === 'mother' ? 'bio_mother' : 'bio_father')
  if (!matchesGuardian) return false
  const hasSpecimen = strOrUndefined(slot.collectionType) !== undefined || strOrUndefined(slot.specimenId) !== undefined
  return !hasSpecimen
}

export function normalizeInboundParentComparators(co: CanonicalOrderPayloadInbound): CanonicalOrderPayload {
  const current = Array.isArray(co.comparators) ? co.comparators : []
  let next = current
  const sides: ParentSide[] = ['mother', 'father']
  for (const side of sides) {
    const slot = co[side]
    if (slot == null || !slotHasMeaningfulIdentity(slot)) {
      continue
    }
    // A primary-guardian-only parent (matches the guardian relationship and has
    // no sample) is surfaced via Guardian Contact, not as a comparator — do not
    // fold them in. The legacy slot key is still stripped below.
    if (slotIsGuardianOnlyParent(co, side, slot)) {
      continue
    }
    const alreadyPresent = next.some((c) => parentSideForComparator(c) === side)
    if (alreadyPresent) {
      continue
    }
    const created = buildComparatorFromSlot(slot, canonicalLiteralForSide(side))
    next = next === current ? [...current, created] : [...next, created]
  }
  // Strip the legacy slot keys so they never reach Redux state.
  if ('mother' in co || 'father' in co) {
    const { mother: _mother, father: _father, ...rest } = co
    return next === current ? rest : { ...rest, comparators: next }
  }
  return next === current ? co : { ...co, comparators: next }
}

/**
 * Return a canonical payload whose comparators all carry a stable client `id`,
 * backfilling `genComparatorId()` on any row that arrived without one.
 *
 * The R5 side-nav dispatches `ensureComparatorIds()` right after hydration to
 * assign these ids in Redux. Because `id` participates in the comparator dirty
 * signature (`serializeComparatorRow`), a baseline captured from the pre-backfill
 * shape would never match the post-backfill store and would latch a permanent
 * phantom "Updates are ready to send". Running the same backfill here — before
 * the baseline is captured and the payload is dispatched — keeps the store and
 * the baseline on identical ids (and makes the side-nav backfill a no-op).
 *
 * Pure: returns the same reference when every comparator already has an id.
 */
export function backfillComparatorIds(co: CanonicalOrderPayload): CanonicalOrderPayload {
  const comparators = co.comparators
  if (!Array.isArray(comparators)) return co
  const needsId = comparators.some((c) => c != null && !(typeof c.id === 'string' && c.id.trim() !== ''))
  if (!needsId) return co
  const next: Comparator[] = comparators.map((c) =>
    c != null && !(typeof c.id === 'string' && c.id.trim() !== '') ? { ...c, id: genComparatorId() } : c
  )
  return { ...co, comparators: next }
}

/**
 * Map a comparator's relationship literal to the parent side it represents
 * (`mother` / `father`), or `null` for non-parent relationships (SIBLING,
 * TWIN_SIBLING, LEGAL_GUARDIAN, etc.). Both canonical UPPER_SNAKE and legacy
 * lowercase literals are accepted. Used by the canonical-order slice and the
 * hydration normalizer to identify the 1-of-1 parent rows.
 */
export function parentSideForComparator(comparator: Pick<Comparator, 'relationship'>): ParentSide | null {
  if (relationshipMatches(comparator.relationship, MOTHER_LITERALS)) return 'mother'
  if (relationshipMatches(comparator.relationship, FATHER_LITERALS)) return 'father'
  return null
}

/**
 * True when a comparator is a parent (mother / father) that should be
 * sequenced: it maps to a parent side, is not explicitly opted out of testing
 * (`isTestingComparator !== false` — absent defaults to testing), and carries a
 * sample (`specimen.collectionType` or `specimen.specimenId`).
 *
 * provider-api decides sequencing from `bool(comparator["isTestingComparator"])`,
 * so the add-comparator persist path uses this to explicitly stamp
 * `isTestingComparator: true` on a testing parent whose flag was only implicit
 * — otherwise the backend treats her as contact-only ("No testing requested").
 */
export function isTestingParentComparator(
  comparator: Pick<Comparator, 'relationship' | 'isTestingComparator' | 'specimen'>
): boolean {
  const hasSample = Boolean(comparator.specimen?.collectionType || comparator.specimen?.specimenId)
  return parentSideForComparator(comparator) != null && comparator.isTestingComparator !== false && hasSample
}
